import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import Tag from 'components/common/tag';
import Tooltip from 'components/layout/tooltip';
import { SystemOptionType } from 'data/types';
import {
  buildSystemTitleTags,
  isSoftBadgeColor,
  SOFT_BADGE_CLASSES,
  TitleTag
} from './system-tags';

const MAX_VISIBLE_TAGS = 3;

type SystemTitleTagsProps = Readonly<{
  system: SystemOptionType;
  showTopMatch: boolean;
  recommended?: boolean;
}>;

export function TitleTagChip({ item }: { item: TitleTag }) {
  if (isSoftBadgeColor(item.color)) {
    return (
      <span
        className={clsx(
          'inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold',
          SOFT_BADGE_CLASSES[item.color]
        )}
      >
        {item.label}
      </span>
    );
  }
  return (
    <Tag
      color={item.color}
      textColor={item.color === 'purple' ? 'white' : 'black'}
      text={item.label}
      icon={item.warningIcon ? ExclamationTriangleIcon : undefined}
    />
  );
}

export default function SystemTitleTags({
  system,
  showTopMatch,
  recommended = false
}: SystemTitleTagsProps) {
  const items = buildSystemTitleTags(system.tags, showTopMatch, recommended);
  if (items.length === 0) return null;

  const visibleItems = items.slice(0, MAX_VISIBLE_TAGS);
  const overflowItems = items.slice(MAX_VISIBLE_TAGS);

  return (
    <div className='flex flex-wrap items-center gap-1'>
      {visibleItems.map((item) => (
        <div key={item.key} className='relative'>
          <TitleTagChip item={item} />
          {item.tooltip && <Tooltip text={item.tooltip} />}
        </div>
      ))}
      {overflowItems.length > 0 && (
        <div className='relative'>
          <Tag color='default' text={`+${overflowItems.length}`} />
          <Tooltip>
            <div className='flex flex-col gap-0.5'>
              {overflowItems.map((item) => (
                <span key={item.key}>{item.label}</span>
              ))}
            </div>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
